import { useMemo, useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { usePresets } from "../../context/PresetsContext";
import { useServices } from "../../context/ServicesContext";

const emptyForm = {
  id: null,
  name: "",
  description: "",
  serviceIds: [],
  active: true,
};

const groupServices = (services = []) => {
  const map = new Map();
  services.forEach((srv) => {
    const category = (srv.category || "Altele").trim() || "Altele";
    if (!map.has(category)) map.set(category, []);
    map.get(category).push(srv);
  });
  return Array.from(map.entries()).map(([category, items]) => ({
    category,
    items: items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  }));
};

export default function PresetsCatalogModal({ open, onClose }) {
  const { presets, loading, error, createPreset, updatePreset, deletePreset, reorderPresets } = usePresets();
  const { services } = useServices();
  const [formState, setFormState] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const activeServices = useMemo(
    () => (services || []).filter((srv) => srv.active !== false),
    [services]
  );
  const grouped = useMemo(() => groupServices(activeServices), [activeServices]);

  const toggleService = (serviceId) => {
    setFormState((prev) => {
      const exists = prev.serviceIds.includes(serviceId);
      return {
        ...prev,
        serviceIds: exists
          ? prev.serviceIds.filter((id) => id !== serviceId)
          : [...prev.serviceIds, serviceId],
      };
    });
  };

  const startNew = () => setFormState(emptyForm);

  const startEdit = (preset) => {
    setFormState({
      id: preset.id,
      name: preset.name || "",
      description: preset.description || "",
      serviceIds: Array.isArray(preset.serviceIds) ? preset.serviceIds : [],
      active: preset.active !== false,
    });
  };

  const handleSave = async () => {
    if (!formState.name.trim()) {
      alert("Completează numele presetului.");
      return;
    }
    if (!formState.serviceIds.length) {
      alert("Selectează cel puțin un serviciu.");
      return;
    }

    const payload = {
      name: formState.name.trim(),
      description: formState.description.trim(),
      serviceIds: formState.serviceIds,
      active: Boolean(formState.active),
      order: formState.id ? undefined : presets.length,
    };

    try {
      setSaving(true);
      if (formState.id) {
        await updatePreset({ id: formState.id, ...payload });
      } else {
        await createPreset(payload);
      }
      setFormState(emptyForm);
    } catch (err) {
      alert(err.message || "Nu s-a putut salva presetul");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (presetId) => {
    if (!presetId) return;
    if (!window.confirm("Ștergi acest preset?")) return;
    try {
      await deletePreset(presetId);
    } catch (err) {
      alert(err.message || "Nu s-a putut șterge presetul");
    }
  };

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = presets.slice();
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    const updates = next.map((preset, idx) => ({ id: preset.id, order: idx }));
    try {
      await reorderPresets(updates);
    } catch (err) {
      alert(err.message || "Nu s-a putut rearanja");
    } finally {
      setDragIndex(null);
    }
  };

  return (
    <Modal title="Preseturi servicii" onClose={onClose} size="xl" contentClassName="max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          {loading && <p className="text-sm text-gray-500">Se încarcă preseturi...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && presets.length === 0 && (
            <p className="text-sm text-gray-500">Nu există preseturi încă.</p>
          )}

          {presets.map((preset, index) => (
            <div
              key={preset.id}
              className="border rounded-xl bg-white shadow-sm p-3 flex flex-wrap items-center justify-between gap-2"
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(index)}
            >
              <div>
                <p className="font-semibold text-sm">{preset.name}</p>
                {preset.description && (
                  <p className="text-xs text-gray-500">{preset.description}</p>
                )}
                <p className="text-xs text-gray-400">
                  {preset.serviceIds.length} servicii
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => startEdit(preset)}>
                  Editează
                </Button>
                <Button size="sm" variant="danger" onClick={() => handleDelete(preset.id)}>
                  Șterge
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="border rounded-xl p-4 bg-gray-50">
          <h3 className="font-semibold text-sm sm:text-base mb-3">
            {formState.id ? "Editează preset" : "Preset nou"}
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500">Nume preset</label>
              <input
                className="w-full border rounded p-2 text-sm"
                value={formState.name}
                onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Descriere (opțional)</label>
              <input
                className="w-full border rounded p-2 text-sm"
                value={formState.description}
                onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="max-h-64 overflow-y-auto border rounded p-2 bg-white">
              {grouped.map((group) => (
                <div key={group.category} className="mb-2">
                  <p className="text-xs font-semibold text-gray-600 mb-1">{group.category}</p>
                  <div className="space-y-1">
                    {group.items.map((srv) => (
                      <label key={srv.id} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={formState.serviceIds.includes(srv.id)}
                          onChange={() => toggleService(srv.id)}
                        />
                        {srv.name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <label className="text-xs flex items-center gap-2">
              <input
                type="checkbox"
                checked={formState.active}
                onChange={(e) => setFormState((prev) => ({ ...prev, active: e.target.checked }))}
              />
              Activ
            </label>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
                {formState.id ? "Salvează" : "Adaugă"}
              </Button>
              <Button size="sm" variant="secondary" onClick={startNew}>
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
